import { type FormEvent, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import type { FetcherWithComponents } from '@remix-run/react';
import { useElements } from '@stripe/react-stripe-js';

import { useUpgradeContext } from '~/context/useUpgradeContext';
import useGiftOrGroupCheckout from '~/pages/upgrade/hooks/useGiftOrGroupCheckout';
import useInternalCheckout from '~/pages/upgrade/hooks/useInternalCheckout';

interface Params {
  fetcher: FetcherWithComponents<unknown>;
  setIsSubmitting: (value: boolean) => void;
  onSuccess: (redirectToSSOLogin: boolean) => void;
  usePaymentElement?: boolean;
}

const useGiftCheckoutSubmitHandler = ({ fetcher, setIsSubmitting, onSuccess, usePaymentElement }: Params) => {
  const { t } = useTranslation();
  const { isInternalCheckout, stripeSession, selectedGiftPrice, email } = useUpgradeContext();
  const elements = useElements();
  const { handleSubmit } = useGiftOrGroupCheckout(isInternalCheckout, t('upgrade.error.card_processing_failed'));
  const { handleSubmitWithPaymentElement } = useInternalCheckout();

  return useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);

      const formData = new FormData(event.currentTarget);
      const purchaserName = formData.get('purchaser_name');
      const recipientEmail = formData.get('recipient_email');

      if (!email) {
        toast.error(t('upgrade.error.email_required'));
        setIsSubmitting(false);
        return;
      }

      if (!purchaserName) {
        toast.error(t('upgrade.tabs.gift.error.purchaser_name_required'));
        setIsSubmitting(false);
        return;
      }

      if (!recipientEmail) {
        toast.error(t('upgrade.tabs.gift.error.recipient_email_required'));
        setIsSubmitting(false);
        return;
      }

      if (isInternalCheckout && (!stripeSession || !elements)) {
        toast.error(t('upgrade.error.stripe_not_ready'));
        setIsSubmitting(false);
        return;
      }

      if (!selectedGiftPrice) {
        toast.error(t('upgrade.error.select_price'));
        setIsSubmitting(false);
        return;
      }

      try {
        if (usePaymentElement && isInternalCheckout) {
          // Narrow nullable values for TypeScript. The earlier guard above
          // already ensures these are present when isInternalCheckout is true,
          // but the type system can't carry that proof.
          if (!stripeSession || !elements) {
            setIsSubmitting(false);
            return;
          }

          await handleSubmitWithPaymentElement({
            event,
            stripeSession,
            elements,
            email,
            defaultErrorMessage: t('errors.default'),
          });

          onSuccess(false);
        } else {
          await handleSubmit(event, fetcher, stripeSession, elements, email);
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : t('upgrade.error.payment_request_failed');
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      t,
      email,
      isInternalCheckout,
      stripeSession,
      elements,
      selectedGiftPrice,
      usePaymentElement,
      fetcher,
      setIsSubmitting,
      onSuccess,
      handleSubmit,
      handleSubmitWithPaymentElement,
    ],
  );
};

export default useGiftCheckoutSubmitHandler;
